/**
 * This file is responsible for rendering the product details page in the application.
 * It fetches and displays the details of a specific product based on the product's URL key.
 *
 * The component uses server-side rendering (getServerSideProps) to fetch the product details and size chart data.
 * If the product does not exist or the API is unable to return a valid response, a not found page is displayed.
 *
 * The component uses the 'next/router' library to access the product URL key from the URL parameters.
 * This key is used to fetch the corresponding product details.
 *
 * The component also handles loading states and errors during the fetching of the product details.
 * If there's an error during the fetch, it is stored in the state and displayed to the user.
 *
 * The Product component from 'hah-fe-core' is used to render the product details.
 * The dataLayerProduct function from 'hah-fe-core' is used to send product details to the data layer for tracking.
 *
 * The component also generates structured data for the product using the getProductStructuredData function,
 * and sets this data in the head of the page for SEO purposes.
 */

import { useEffect, useRef } from 'react';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import languageParser from 'accept-language-parser';
import {
  publicQuery,
  UrlResolver,
  GetCmsBlocks,
} from '@Hotels-at-Home/hah-fe-core/dist/services/graphql';
import {
  getProduct,
  getProductDynamic,
} from '@Hotels-at-Home/hah-fe-core/dist/store/product/thunks';
import { selectProduct } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/product';
import {
  getCookie,
  extractErrorMessage,
  parseJSON,
  recordCacheVariant,
} from '@Hotels-at-Home/hah-fe-core/dist/utils';
import { createNoCacheRedirect } from '@Hotels-at-Home/hah-fe-core/dist/utils/redirectResponse';
import { dataLayerProduct } from '@Hotels-at-Home/hah-fe-core/dist/utils/dataLayer';
import Product from '@Hotels-at-Home/hah-fe-core/dist/components/Product';
import { LanguageCode, COOKIE_LOCALITY_NAME } from '@Hotels-at-Home/hah-fe-core/dist/constants';
import {
  ResolvedItemType,
  IUrlResolverRequest,
  IUrlResolverResponse,
  TServerSideProps,
  TSizeChart,
  IBaseProduct,
} from '@Hotels-at-Home/hah-fe-core/dist/types';

import { wrapper } from '~/store';
import { useDispatch, useSelector } from '~/store/hooks';
import {
  getRouteProductLink,
  getProductStructuredData,
  getBreadcrumbListStructuredData,
  getProductUrlKeyFromPath,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/product';

import {
  getHomepageRedirect,
  getRedirectProduct,
  shouldRedirect,
} from '@Hotels-at-Home/hah-fe-core/dist/utils/redirect';
import {
  HrefMap,
  filterExternalHrefData,
  generateEuHrefData,
  getHrefData,
  homepageHrefLangTags,
} from '~/utils/hreflangs';
import { NextPage } from '@Hotels-at-Home/hah-fe-core/dist/components';

import apolloConfig from '~/constants/apolloConfig';
import {
  productNameFilters,
  siteName,
  parentHotelCompany,
  catalogId,
  region,
} from '~/constants/siteConfig';
import { selectCurrentCurrencyCode } from '@Hotels-at-Home/hah-fe-core/dist/storeSelectors/currencies';
import logger from '@Hotels-at-Home/hah-fe-core/dist/utils/logger';

type TProps = TServerSideProps<TSizeChart> & {
  languageUrls?: {
    href: string;
    value: string;
  }[];
};

interface HrefDataProduct {
  [key: string]: any;
}

const ProductPage = (props: TProps) => {
  const { t } = useTranslation('', { keyPrefix: '' });
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { error, size_chart_data, languageUrls } = props;
  const { locale, query, asPath } = useRouter();
  const path = query.path as string[];

  const product = useSelector(selectProduct);
  const currentCurrency = useSelector(selectCurrentCurrencyCode);

  const dispatch = useDispatch();

  const productComplete: IBaseProduct = { ...product, size_chart_data: { ...size_chart_data } };

  // Extract product "urlKey" from url
  const urlKey = getProductUrlKeyFromPath(path);

  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;

  const meta = {
    title: product?.meta_title || `${t('$local.$meta.meta_title')} - ${product?.name}`,
    description: product?.meta_description,
    keyword: product?.meta_keyword,
    url: `${siteUrl}/${locale}${asPath}`,
    image: product?.image?.url,
  };

  const structuredData = getProductStructuredData(product, locale);
  const breadcrumbListStructuredData = getBreadcrumbListStructuredData(product, locale);

  const hrefData = getHrefData(product, locale);
  const externalHrefData = filterExternalHrefData(hrefData);
  const euHrefData = generateEuHrefData(languageUrls, siteUrl, '/p/');

  const canonicalUrl = `${siteUrl}/${locale}${new URL(asPath, siteUrl).pathname}`;
  const lastFetchedRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  const pendingFetchRef = useRef<{ fetchKey: string; urlKey: string } | null>(null);

  useEffect(() => {
    const runQueuedFetch = () => {
      const nextFetch = pendingFetchRef.current;
      if (!nextFetch || nextFetch.fetchKey === lastFetchedRef.current) return;

      isFetchingRef.current = true;
      pendingFetchRef.current = null;

      dispatch(getProductDynamic({ urlKey: nextFetch.urlKey }))
        .then(resultAction => {
          if (getProductDynamic.rejected.match(resultAction)) {
            const err = resultAction.payload.errorMessage;

            if (err === 'notFound') {
              console.error('Product not found');
            }
          }
        })
        .finally(() => {
          lastFetchedRef.current = nextFetch.fetchKey;
          isFetchingRef.current = false;
          runQueuedFetch();
        });
    };

    const fetchKey = `${urlKey}-${locale}-${currentCurrency}`;
    if (lastFetchedRef.current === fetchKey) return;

    pendingFetchRef.current = { fetchKey, urlKey };
    if (isFetchingRef.current) return;
    runQueuedFetch();
  }, [locale, currentCurrency, urlKey, dispatch]);

  useEffect(() => {
    dataLayerProduct({
      product,
      affiliation: parentHotelCompany,
      brand: siteName,
      locale: locale as LanguageCode,
      siteFilters: productNameFilters,
    });
  }, [product?.sku]);

  return (
    <NextPage
      error={error}
      meta={meta}
      headContent={
        <>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbListStructuredData) }}
          />
        </>
      }
      siteUrl={siteUrl}
    >
      <Head>
        <link rel="canonical" href={canonicalUrl} />
        {euHrefData?.map(
          ({ href, url, rel }) =>
            href && <link key={`${href}`} rel={rel} hrefLang={href} href={url} />,
        )}
        {externalHrefData?.map(
          ({ href, hreflang, rel }) =>
            href && <link key={`${hreflang} ${rel}`} rel={rel} hrefLang={hreflang} href={href} />,
        )}
      </Head>
      <Product product={productComplete} />
    </NextPage>
  );
};

export const getServerSideProps: GetServerSideProps = wrapper.getServerSideProps(
  store => async ctx => {
    const props: TProps = {
      ...(await serverSideTranslations(ctx.locale)),
      size_chart_data: null,
      error: null,
    };
    let notFound = false;
    const localityParam = ctx.query.locality; // can be used to override the locality header for testing
    const localityHeader =
      (ctx.req.headers.Locality as string) || (ctx.req.headers.locality as string);
    const desiredLocale = localityParam ? localityParam.toString().toUpperCase() : localityHeader;
    const path = ctx.query.path as string[];
    const url = path.join('/');
    const userLanguage = ctx.req.headers['accept-language'] as string;

    const emitCacheObservability = (statusCode: number, contentCurrency?: string) => {
      try {
        recordCacheVariant({
          hook: 'product_getServerSideProps',
          routePattern: '/[locale]/p/[...path]',
          site: catalogId,
          req: ctx.req,
          res: ctx.res,
          locale: ctx.locale,
          query: ctx.query,
          resolvedUrl: (ctx as any).resolvedUrl || ctx.req?.url,
          statusCode,
          observed: {
            contentCurrency,
          },
          expectedRequestKind: 'document',
        });
      } catch (error) {
        logger(`[cache-observability] product_getServerSideProps failed: ${String(error)}`, {
          type: 'error',
          category: 'api',
          skipConsole: true,
        });
      }
    };

    // Redirect based on URL key
    // Checks if we need to redirect, if the URL key in the browser is different from the one in the response
    // Needs validation: I believe this hooks into Magento's "URL Rewrites" functionality
    // Current has the following bug:
    // 1. With cookies disabled, go to https://staging-eu.shopmarriott.com/en/p/bedding/pillows/the-marriott-pillow?referrer=internal
    // 2. You will be redirected to https://www.shopmarriott.com/product.aspx?the-marriott-pillow
    try {
      const resolvedProduct = await publicQuery<IUrlResolverResponse, IUrlResolverRequest>(
        apolloConfig(ctx.locale as LanguageCode),
        {
          query: UrlResolver,
          variables: {
            url,
          },
        },
        'UrlResolver',
        ctx,
      ).then(res => res.data.urlResolver);

      // if the resolvedProduct.language_urls object contains a key "__typename", remove it from the object
      if (Object.prototype.hasOwnProperty.call(resolvedProduct.language_urls, '__typename')) {
        // eslint-disable-next-line no-underscore-dangle
        delete (resolvedProduct.language_urls as any).__typename;
      }

      const resolvedProductArray = Object.entries(resolvedProduct.language_urls);

      props.languageUrls = resolvedProductArray.map(([key, value]) => ({
        href: key.replace('url_', ''),
        value,
      }));

      const resolvedItemType = resolvedProduct?.type;
      const resolvedProductId = resolvedProduct?.id;
      const resolvedProductUrl = resolvedProduct?.relative_url;
      const { query } = ctx;

      if (resolvedItemType !== ResolvedItemType.Product || !resolvedProductId) {
        props.error = `Product was not found: ${path}`;
        notFound = true;
        emitCacheObservability(404);
        return { props, notFound };
      }

      // If current "productUrl" and response "productUrl" are differ -
      // make a redirect to a new "productUrl"
      if (resolvedProductUrl && resolvedProductUrl !== url) {
        let destination = getRouteProductLink(resolvedProductUrl, ctx.locale);
        if ('referrer' in query) {
          destination += `?referrer=${query.referrer}`;
        }
        if ('___store' in query) {
          destination += `?___store=${catalogId}${region}`;
        }
        emitCacheObservability(307);
        return createNoCacheRedirect(ctx.res, destination);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }

    // Extract product "urlKey" from url
    const urlKey = getProductUrlKeyFromPath(path);

    const resultAction = await store.dispatch(getProduct({ urlKey, ctx }));
    const localityCookie = getCookie(COOKIE_LOCALITY_NAME, ctx);
    const resolvedProductPayload = getProduct.fulfilled.match(resultAction)
      ? (resultAction.payload as IBaseProduct)
      : null;

    if (getProduct.rejected.match(resultAction)) {
      let err = resultAction.payload.errorMessage;

      if (err === 'notFound') {
        notFound = true;
        err = '$common.$validation.productNotFound';
      }

      props.error = err;
    }

    // Redirect based on IP address
    if (shouldRedirect(ctx) && resolvedProductPayload) {
      const parsedLanguage = languageParser.parse(userLanguage)[0];
      const hrefDataProduct: HrefDataProduct = getHrefData(
        resolvedProductPayload,
        ctx.locale,
        true,
      );
      const hrefRedirectToLocale = getRedirectProduct(
        (localityCookie as string) || (desiredLocale as string),
      );

      // TODO: Refactor to include in the same flow as en-us
      if (parsedLanguage?.code === 'zh') {
        const newRedirect = `${parsedLanguage.code}-cn`;

        const hrefData = hrefDataProduct.find(
          (item: { hreflang: string }) => item.hreflang === newRedirect,
        );

        if (typeof hrefData?.href === 'string') {
          emitCacheObservability(307);
          return createNoCacheRedirect(ctx.res, hrefData.href);
        }
      }

      // Only redirect if going outside EU; Internal redirects are handled in the middleware
      if (
        hrefRedirectToLocale !== 'en-gb' &&
        hrefRedirectToLocale !== 'fr' &&
        hrefRedirectToLocale !== 'de' &&
        hrefRedirectToLocale !== 'es'
      ) {
        const redirectUrl = hrefDataProduct.find(
          (item: HrefMap) => item.hreflang === hrefRedirectToLocale,
        );

        if (redirectUrl) {
          emitCacheObservability(307);
          return createNoCacheRedirect(ctx.res, redirectUrl.href);
        }
        // no Referral and non EU IP
        // HREF data null > redirect
        const hpRedirect = getHomepageRedirect(localityCookie as string);
        const hpHrefData = homepageHrefLangTags.find(item => item.hrefLang === hpRedirect);
        emitCacheObservability(307);
        return createNoCacheRedirect(ctx.res, hpHrefData.href);
      }
    }

    try {
      props.size_chart_data = await publicQuery(
        apolloConfig(ctx.locale as LanguageCode),
        {
          query: GetCmsBlocks,
          variables: {
            locale: ctx.locale,
            identifiers: ['size_chart_data'],
          },
        },
        'GetCmsBlocks',
        ctx,
      ).then(res => parseJSON(res.data.cmsBlocks.items[0]?.content));
    } catch (e) {
      props.error = extractErrorMessage(e);
    }

    try {
      const contentCurrency = (resultAction.payload as IBaseProduct)?.price_range?.maximum_price
        ?.final_price?.currency;
      let finalStatusCode = 200;
      if (notFound) {
        finalStatusCode = 404;
      } else if (props.error) {
        finalStatusCode = 500;
      }
      emitCacheObservability(finalStatusCode, contentCurrency);
    } catch (error) {
      logger(`[cache-observability] product final emit failed: ${String(error)}`, {
        type: 'error',
        category: 'api',
        skipConsole: true,
      });
    }

    return { props, notFound };
  },
);

export default ProductPage;
